<template>

  <ion-page id="blindfold-page">
    <AlertReplacement :alertInfo="alertInfo" :trigger="alertTrigger"
              @alert-result="alertResult"></AlertReplacement>
 
    <ion-content>

      <div v-show="showTour">
        <Tour  :tour="tourInfo" :tourTrigger="tourTrigger" @tour-done="tourDone" />
      </div>

      <div id="the_board" :style="styleBoard">
        <CMChessboard  :fen="forceFen" :validMoves="availableMoves" :orientation="orientation" 
                  :boardSize="boardSize" :animateOnePly="animateFen" 
                  :reportFromTo="showEyegazeButtons" 
                  :animateSequence="replayFenArray"
                  :pieceSet="pieceSet"
                  @board-move="boardMove" 
                  @move-animated="moveAnimated"
                  />
      </div>

      <div v-show="showState == SHOW_PLAY"
                :class="{ 'text-dark' : isDark,'text-light' : !isDark }" >
        <div id="the_statusline"  :style="styleInstruction">
          <div v-if="!showEyegazeButtons" v-html="instructionString"></div>
          <div v-show="moveHistoryStack.length > 0" style="text-align:left;">
            <MoveHistory :moveHistoryStack="moveHistoryStack" :trigger="moveHistoryTrigger"
                        :firstMoveNumber="firstMoveNumber" :prefixString="replayTextString"
            />
          </div>
        </div>

        <!-- button matrix -->
        <div v-if="showEyegazeButtons" >
          <div id="eyegaze_area" :style="styleEyeGaze">
            <div v-if="showEyegazeButtons && gameState != IN_GAME" style="text-align:center;" v-html="instructionString">
            </div>
            <div style="display: inline;">
              <div v-for="row in eyegazeMatrix" >
                  <span v-for="col in row.items" >
                    <span v-if="col != null">
                      <span >
                        <ion-button @click="eyegazeClick(col)" 
                              :class="{ 'one-move-button' : col.moves.length == 1,
                                        'multiple-move-button' : col.moves.length > 1,
                                        'single-height' : eyegazeMatrix.length == 4,
                                        'three-fourth-height' : eyegazeMatrix.length == 3,
                                        'double-height' : eyegazeMatrix.length <= 2 }">
                            <ion-img class="chess-image" style="text-align: left;" :src="col.img" alt="Chessvis"></ion-img>
                          <span class="button-text">   {{ col.info }}</span>
                        </ion-button>
                      </span>
                    </span>
                  </span>
              </div>
            </div>
          <div >
        </div>
      </div>
      </div>
      </div>
      <div v-show="showState == SHOW_REVIEW">
        <div :class="{ 'text-dark' : isDark,'text-light' : !isDark }" 
            :style="styleReview">
          <GameReviewMoveList 
                      :gameId="reviewGameId"
                      :directionClicked="directionClicked" 
                      :directionTrigger="directionTrigger"
              @move-list-click="moveListClick"
            >
          </GameReviewMoveList>
        </div>
       </div>
       <div v-if="!showBigButtons">
        <ActionSheet :heading="headingString" :items="actionSheetButtons" 
                      :trigger="actionSheetTrigger"
                      @action-sheet-result="actionSheetResult">
        </ActionSheet>
      </div>

    </ion-content>
    <Toolbar :elements="toolbarElements" :trigger="toolbarTrigger" :ignoreClicks="showTour"
                          @toolbar-click="toolbarClick"/>
  </ion-page>
</template>

<script setup>

  import { IonGrid, IonRow,IonCol,IonImg,IonButton,IonIcon,IonPage, IonContent,modalController  } from '@ionic/vue';

  import AlertReplacement from '@/components/AlertReplacement.vue'
  import ActionSheet from '@/components/ActionSheet.vue'
  import ModalErrorMessage from '@/components/ModalErrorMessage.vue'

  import CMChessboard from '@/components/CMChessboard.vue'

  import { Chess } from '@/ts/chess.ts'

  import { buildPieceLocationStrings, getPieceSetName, columnToNumber, squarePairToAlgebraic,
    buildAvailableMoves, buildEyegazeButtons } from '@/composables/blindfoldSupport.js'

  import { t } from '@/composables/translateText.js'

  import GameReviewMoveList from '@/components/GameReviewMoveList.vue'
  import { findToolbarElement, setToolbarElementDisabledFlag, setToolbarElementLabel} 
                from '@/composables/toolbarSupport.js'
  import ModalGenericSingleList from '@/components/ModalGenericSingleList.vue'

  import { findValueString } from '@/composables/blindfoldSupport.js'

  import Toolbar from '@/components/Toolbar.vue';

  import party from "party-js";

  import { onMounted, onBeforeUnmount } from 'vue'
  import { ref, watch } from 'vue'
  import { useGetData } from '@/composables/getData.js'
  import { useConnectionStore } from '@/stores/connectionStore';

  import { doStartupCheck } from '@/composables/startupCheck.js'
  import { availableWidthHeight, calculateChessBoardRect , isScreenPortraitOrientation} from '@/composables/screenSize'
  import { useWindowSizeStore } from '@/stores/windowSizeStore';
  import Tour from '@/components/Tour.vue'
  import MoveHistory from '@/components/MoveHistory.vue'
  import ModalActionReplacement from '@/components/ModalActionReplacement.vue';
  import ModalAlertReplacement from '@/components/ModalAlertReplacement.vue';

  const emit = defineEmits(['menu-item-click','menu-help-item-click'])

  const SHOW_PLAY = 0
  const SHOW_REVIEW = 1

  const showState = ref(SHOW_PLAY)

  const NO_GAME = 0
  const IN_GAME = 1
  const GAME_ENDED = 2

  const directionClicked = ref("")
  const directionTrigger = ref(0)

  var gameState = ref(NO_GAME)

  const reviewGameId = ref("")

  const TOOLBAR_START_GAME = 0
  const TOOLBAR_TAKEBACK_OPTIONS = 1
  const TOOLBAR_REVIEW = 2
  const TOOLBAR_REVEAL = 3
  const TOOLBAR_DONE = 4

  const TOOLBAR_REVIEW_REWIND = 20
  const TOOLBAR_REVIEW_BACK = 21
  const TOOLBAR_REVIEW_NEXT = 22
  const TOOLBAR_REVIEW_LAST = 23
  const TOOLBAR_REVIEW_DONE = 24

  const ACTION_BLINDNESS = 11
  const ACTION_ENGINE = 12
  const ACTION_SKILL = 13
  const ACTION_COLOR = 14
  const ACTION_REVEAL = 15

  const alertInfo = ref({})
  const alertTrigger = ref(0)

  const actionSheetTrigger = ref(0)
  var actionSheetButtons = []

  const INITIAL_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"

  const INSTRUCTION_FONTSIZE = 16

  const connectionStore = useConnectionStore()

  const replayTextString = ref(t("Replay") + ": ")
  const headingString = ref(t("Options"))
  const windowSizeStore = useWindowSizeStore()
  const noAvailableMovesFlag = ref(0)
  const windowSizeRef = ref({ width : windowSizeStore.getWindowSize['width'],
                        height : windowSizeStore.getWindowSize['height'] })
  //console.log("**** explorer initial screensize", windowSizeRef.value)
  const boardSize = ref(100)

  const whichMenu = localStorage.getItem("whichMenu")
  const showBigButtons = ref(whichMenu == "buttons" || connectionStore.getShowBigButtons)
  const showEyegazeButtons = ref(connectionStore.getIsEyeGaze)

  const menuTrigger = ref(0)

  var blankAppendTrigger = ""

  var moveCompleteHistoryStack = []
  const moveHistoryStack = ref([])

  const moveHistoryTrigger = ref(0)
  const firstMoveNumber = ref(1)
  var fenBeforeMoveStack = INITIAL_FEN
  var replayMoveStack = []
  const replayFenArray = ref([])

  // check player's SAN after making move, save flag if it is a checkmate
  // look at flag when applying engine move
  var playerMoveCheckMated = false

  const tourTrigger = ref(0)
  const tourOption = connectionStore.getTourOption
  //console.log("*** Tour option",tourOption)
  const showTour = ref((tourOption == "show"))
  
  var playType = connectionStore.getProductOption
  const isSubscriber = ref(connectionStore.getIsSubscriber == 1)

  const isDark = ref(connectionStore.getDarkmode != "light")

  const toolbarElements = ref([])

  const initialFen = ref(INITIAL_FEN)
  const forceFen = ref(INITIAL_FEN)
  var fenOfCurrentBoard = INITIAL_FEN

  var isPortrait = true

  const animateFen = ref([])
  const pieceSet = ref("regular")
  var savePieceSet = pieceSet.value

  var fenAfterLastEngineMove = ""
  var boardIsRevealed = false

  var playingAsWhite = 1
  var revealEveryMoveNumber = 0
  var orientation = ref("white")

  const availableMoves = ref([])
  var chess = new Chess()

  var replayHistorySans = []
  var replayHistoryFens = []
  var fenBeforeReplay = ""
  var firstReplayMoveNumber = 1

  var availableMovesForTakeback = []

  const toolbarTrigger = ref(0)

  const instructionString = ref("")

  const eyegazeMatrix = ref([[]])
  var blindnessLevel = 1

  var openGameId = 0

  // if continuing game, grab the gameId and if it was blindfold or computer
  if (playType == "continue") {
    openGameId = connectionStore.getPlayComputerGameId
    playType = connectionStore.getPlayComputerBlindfold
  }

  var moveNumber = 0
  var canTakeBack = false
  var canUndoBigButtonSelection = false
  var playingAsWhite = 1
  var engineMoveJson = null
  var engineMoveToRepeat = ""

  var optionDisplayArray = []
  var optionDisplaySelected = ""
  var optionHeading = ""


  // which option is being worked on in generic list
  var whichOptionIsGenericList = ""

  // hardcoded option strings
  const blindfoldStrings = (isSubscriber.value) ? [t("Regular Pieces"),t("One Color Pieces"),t("Two Color Discs"),t("One Color Disc"),t("Empty Board")]
              : [t("One Color Pieces"), t("Two Color Discs")]
  const blindfoldValues = (isSubscriber.value) ? [ "1", "2", "3", "4", "5" ] : [ "2", "3"]
  var currentBlindfold = ""
  // var blindnessCurrentValue = 1
  // var blindnessCurrentValueString = blindnessChoiceStrings[0]

  const colorStrings = [t("Play as White"), t("Play as Black")]
  const colorValues = [ "1", "0" ]
  var currentColor = ""

  // var colorCurrentValue = 1
  // var colorCurrentValueString = colorChoiceStrings[0]

  var currentEngine = ""
  var engineNameStrings = [ "Komodo"]
  var engineStrings = [ "Komodo - "+ t("Best To Start")]
  var engineValues = [ "2"]

  if (isSubscriber.value) {
    engineNameStrings.push("Stockfish")
    engineStrings.push("Stockfish")
    engineValues.push("1")

    engineNameStrings.push("Maia")
    engineStrings.push("Maia - " + t("Tuned to Lichess ratings"))
    engineValues.push("3")
  }

  // var engineCurrentValue = 2
  // var engineCurrentValueString = engineChoiceStrings[0]

  // build skill strings
  const skillTranslation = t("Skill")
  var skillStrings = []
  var skillValues = []
  for (var i=0; i <= 20; i++) {
    const komodo = 150 + i * 150
    const stockfish = 1350 + i * 80
    const maia = 600 + i * 100
    const string = skillTranslation + " " + i + " - Komo(" + komodo + "), Sf(" + stockfish + "), Maia(" + maia + ")"
    skillStrings.push(string)
    skillValues.push(String(i))

    // free version only has the zero skill level
    if (isSubscriber.value == false)
      break
  }
  var currentSkill = ""

  // var skillCurrentValue = 0
  // var skillCurrentValueString = skillChoiceStrings[0]

  // current option values. Save them so they can show on the big button Action Sheet Repplacement
  var blindfoldString = ""
  var colorString = ""
  var engineString = ""
  var skillString = ""
  var revealString = ""

  // build takeback strings
  const secTranslation = t("sec")
  const secsTranslation = t("secs")
  var takebackStrings = []
  var takebackValues = []
  for (var i=500, j=0.5; i <= 5000; i += 500, j += 0.5) {
    const secString = (i <= 1000) ? secTranslation : secsTranslation
    const string = j.toFixed(1) + " " + secString
    takebackStrings.push(string)
    takebackValues.push(String(i))
  }
  var currentTakeback = ""

  const revealStrings = [t("Never Reveal Board"), 
              t("Reveal Every") + " 3 " + t("Moves"),
              t("Reveal Every") + " 5 " + t("Moves"),
              t("Reveal Every") + " 7 " + t("Moves"),
              t("Reveal Every") + " 10 " + t("Moves")
  ]
  const revealValues = [ "0", "3", "5", "7", "10" ]
  var currentReveal = ""

  const styleBoard = ref({
    position : "absolute",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleInstruction = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px",
    "font-size" : INSTRUCTION_FONTSIZE + "px",
    "text-align" : "center",
    "line-height" : "1.2em",
    "margin-top" : "0px"
  })

   const styleReview = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px",
    "font-size" : INSTRUCTION_FONTSIZE + "px",
    "text-align" : "center",
    "line-height" : "1.2em",
  })

   const styleEyeGaze = ref({
    position : "absolute",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })
  
  // tour stuff

  const computerSubscriberIntro = { id: "", title: t("Play the Computer"), text : t("This is straight up - 'Play the Computer'.<br><br>The game will start by randomly selecting moves in the opening tree based on popularity. This gives a much more realistic start to the game than if the engine was used from move 1")}
  const computerFreeIntro = { id: "", title: t("Play the Computer"), text : t("This is straight up - 'Play the Computer'")}

  const blindfoldSubscriberIntro = { id: "", title: t("Blindfold Chess"), text : t("Chessvis has many features to help ease your way into 'Blindfold Chess'.<br><br>There are four graduated blindfold 'Stepping Stones', two engines and 21 engine skill levels")}
  const blindfoldSubscriberSteppingStones = { id: "", title: t("Stepping Stones"), text : t("You can select from the 4 graduated blindfold levels:<br><br>- One shared piece color<br>- White and black disks<br>- One shared disk color<br>- No visible pieces at all")}
  const subscriberOpponent = { id: "", title: t("Engines"), text : t("You can choose between the Komodo, Stockfish and Maia engines. All are extremely strong at high skill levels. The Komodo simulates a much weaker player at the lower skill levels. Maia is tuned to Lichess players at the rating")} 

  const blindfoldFreeIntro = { id: "", title: t("Blindfold Chess"), text : t("The free version of Chessvis lets you give blindfold chess a try with two of the 4 'stepping stones'")}
  const blindfoldFreeSteppingStones = { id: "", title: t("Stepping Stones"), text : t("Choose between all the pieces being the same color. I.e., you know what the piece is but need to remember if it yours or your opponent's.<br><br>Or two color disks - you know the color of the pieces but you need to remember what the piece is")}
  const blindfoldReveals = { id: "", title: t("Spaced Reveals"), text : t("The 'Blindfold King' recommends to start that you think about the game as a series of several move sequences.<br><br>Focus on remembering the pieces from one reveal to another. Is there another program that offers this?")}
  const freeOpponent = { id: "", title: t("Playing the Engine"), text : t("You'll be playing against the Komodo engine at its basic skill level.<br><br>Unique to Chessvis is that opening moves come from the database and are randomly selected based on popularity. This gives a much better start then just the engine alone.")}

  const computerFreeSubscriberBenefits = { id: "", title: t("Subscriber Benefits"), text : t("Subscribers get 'Stockfish' and 'Maia' as additional chess engines and can play all at any strength level.<br><br>Please consider subscribing and supporting my work.")}
  const blindfoldFreeSubscriber1Benefits = { id: "", title: t("Subscriber Benefits"), text : t("Subscribers get two additional 'stepping stones' including: one color disks and the empty board.")}

  const blindfoldFreeSubscriber2Benefits = { id: "", title: t("Subscriber Benefits"), text : t("Subscribers also get 'Stockfish' as a 2nd chess engine and can play either one at any strength level.<br><br>Please consider subscribing and supporting my work.")}

  const blindfoldReview = { id: "", title: t("Game Review"), text : t("Games are stored on the server and can be reviewed immediately or later.")}

  const toolbarStartGame = { id: "the_toolbar", title: t("Start Game - Toolbar"), text : t("Start and End Game with this button")}
  const toolbarTakeback = { id: "the_toolbar", title: t("Takeback - Toolbar"), text : t("You can set the 'delay' before the opponent's move is shown. If you made a wrong move, you have that amount of time to 'take it back'.")}
  const toolbarReview = { id: "the_toolbar", title: t("Review - Toolbar"), text : t("Once the game is ended, you can immediately switch to the 'Review' screen.<br><br>Games are stored and can be reviewed at anytime off the 'Game History' screen.")}
  const toolbarReveal = { id: "the_toolbar", title: t("Reveal - Toolbar"), text : t("As an alternative to the '<i>Reveal Every So Many Moves</i>', you can reveal the board on demand.")}
  const toolbarFreeBlindness = { id: "the_toolbar", title: t("Blindfold Level Option - Toolbar"), text : t("The free version enables you to try blindfold chess at two levels.<br><br><b>One Color Pieces</b> - Both sides will show as white<br><br><b>Two Color Disks</b> - All pieces will be white or black disks")}
  const toolbarSubscriberBlindness = { id: "the_toolbar", title: t("Blindfold Level Option - Toolbar"), text : t("Select among the four blindfold stepping stones.")}
  const toolbarBlindfoldEngine = { id: "the_toolbar", title: t("Engine & SKill Options - Toolbar"), text : t("Choose between the Komodo, Stockfish and Maia computer engines at skill levels 0 to 20.<br><br>Note: the Stockfish engine even at level 0 is still playing real chess. (At least to me!)<br><br>Komodo at level 0 makes random moves. Maia is turned to players at the rating.")}  
  const toolbarComputerEngine = { id: "the_toolbar", title: t("Engine & Skill Options - Toolbar"), text : t("Choose between the Komodo, Stockfish and Maia computer engines at skill levels 0 to 20.<br><br>Note: the Stockfish engine even at level 0 is still playing real chess. (At least to me!)<br><br>Komodo at level 0 makes random moves. Maia is turned to players at the rating.")}
 
  const toolbarColor = { id: "the_toolbar", title: t("Color You Play Option - Toolbar"), text : t("Play with the white or black pieces")}  
  const toolbarRevealOption = { id: "the_toolbar", title: t("Reveal Board - Toolbar"), text : t("The 'Blindfold Chess King' reccomends starting off revealing the board every # of moves.<br><br>This way you're not trying to remember the entire game but just so many moves. The idea is to increase this number as you get better.")}

  const blindFreeTourInfo = {
    id : "blindfoldFree",
    video : "",
    elements : [
      blindfoldFreeIntro,
      blindfoldFreeSteppingStones,
      blindfoldReveals,
      freeOpponent,
      blindfoldFreeSubscriber1Benefits,
      blindfoldFreeSubscriber2Benefits,
      blindfoldReview,
      toolbarStartGame,
      toolbarTakeback,
      toolbarReview,
      toolbarReveal,
      toolbarFreeBlindness,
      toolbarColor,
      toolbarRevealOption
        ]
  }

  const blindSubscriberTourInfo = {
    id : "blindfoldSubscriber",
    video : "",
    elements : [
      blindfoldSubscriberIntro,
      blindfoldSubscriberSteppingStones,
      blindfoldReveals,
      subscriberOpponent,
      blindfoldReview,
      toolbarStartGame,
      toolbarTakeback,
      toolbarReview,
      toolbarReveal,
      toolbarSubscriberBlindness,
      toolbarBlindfoldEngine,
      toolbarColor,
      toolbarRevealOption
        ]
  }

  const computerSubscriberTourInfo = {
    id : "computerSubscriber",
    video : "",
    elements : [
      computerSubscriberIntro,
      subscriberOpponent,
      blindfoldReview,
      toolbarStartGame,
      toolbarTakeback,
      toolbarReview,
      toolbarComputerEngine,
      toolbarColor       ]
  }

  const computerFreeTourInfo = {
    id : "computerFree",
    video : "",
    elements : [
      computerFreeIntro,
      freeOpponent,
      computerFreeSubscriberBenefits,
      blindfoldReview,
      toolbarStartGame,
      toolbarTakeback,
      toolbarReview,
      toolbarColor
    ]
  }

  const tourInfo = ref(blindFreeTourInfo)

  onMounted(() => {
        updateWidthHeights()
   })
   
  buildToolbar()

  doStartupCheck(false,afterStartupCallbackRoutine)

  const openErrorModal = async (programName,errorMessage) => {
    const modal = await modalController.create({
      component: ModalErrorMessage,
      componentProps: {
        type : "commError",
        program : programName ,
        errorMessage : errorMessage
      }
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();
  };

  function afterStartupCallbackRoutine(stuffChanged,errorProgram,errorEntry) {
    if (errorProgram != null)
      openErrorModal(errorProgram,errorEntry)

    updateWidthHeights()
    if (openGameId > 0)
      getOpenGame()
    else
      getUserInfo()
    menuTrigger.value++
  }

  function buildActionSheetButtons() {

    actionSheetButtons = []
    if (playType == "blindfold")
      actionSheetButtons.push({text : t("Blindfold Level"), code : "blindfold",
                current: blindfoldString, data : { index : ACTION_BLINDNESS}})

    //console.log("is subscriber",isSubscriber.value)
    if (isSubscriber.value) {
      actionSheetButtons.push({text : t("Engine for Opponent"), code : "engine",
                current : engineString , data : { index : ACTION_ENGINE}})
      actionSheetButtons.push({text : t("Engine Strength"), code : "skill",
                current : skillString, data : { index : ACTION_SKILL}})
    }

    actionSheetButtons.push({text : t("Color You Play"), code : "color",
                current : colorString, data : { index : ACTION_COLOR}})

    if (playType == "blindfold")
      actionSheetButtons.push({text : t("Reveal Board"), code : "reveal",
                current : revealString, data : { index : ACTION_REVEAL}})

    if (showBigButtons.value == false)
      actionSheetButtons.push({ text: t('Cancel'), role: 'cancel',code : "cancel" , current : "", data: { index : -1}})

  }

  function buildToolbar() {

    toolbarElements.value = []
    actionSheetButtons = []

    if (showState.value == SHOW_REVIEW) {
      toolbarElements.value = [
        { index : TOOLBAR_REVIEW_REWIND, label : t("Rewind") ,disabled : true},
        { index : TOOLBAR_REVIEW_BACK, label : t("Back") ,disabled : true},
        { index : TOOLBAR_REVIEW_NEXT, label : t("Next") ,disabled : true},
        { index : TOOLBAR_REVIEW_LAST, label : t("Last") ,disabled : true},
        { index : TOOLBAR_REVIEW_DONE, label : t("Done") ,disabled : false},
      ]
      return
    }

    // pick the right tour info
    if (playType == "blindfold")
      tourInfo.value = (isSubscriber.value) ? blindSubscriberTourInfo : blindFreeTourInfo
    else
      tourInfo.value = (isSubscriber.value) ? computerSubscriberTourInfo : computerFreeTourInfo


    toolbarElements.value.push({ index : TOOLBAR_START_GAME, label : t("Start Game") , disabled : false})

    if (gameState.value == IN_GAME)
      toolbarElements.value.push({ index : TOOLBAR_TAKEBACK_OPTIONS, label : t("Takeback") , disabled : true})
    else
      toolbarElements.value.push({ index : TOOLBAR_TAKEBACK_OPTIONS, label : t("Options") , disabled : false, hasAction : true})
    toolbarElements.value.push({ index : TOOLBAR_REVIEW, label : t("Replay") ,disabled : true})

    if (playType == "blindfold")
      toolbarElements.value.push({ index : TOOLBAR_REVEAL, label : t("Reveal") ,disabled : true, 
                                          collapse :true})
    if (whichMenu == "buttons")
      toolbarElements.value.push({ index : TOOLBAR_DONE, label : t("Done") , disabled : false})

    // OPTIONS Action sheet.  First two are for everyone, then append as needed
  
    toolbarTrigger.value++

    buildActionSheetButtons()
  }

  function updateStyleObjects(boundaries) {
    boardSize.value = boundaries['board'].height

    styleBoard.value.height = boundaries['board'].height + "px"
    styleBoard.value.width = boundaries['board'].width + "px"
    styleBoard.value.top = boundaries['board'].top + "px"
    styleBoard.value.left = boundaries['board'].left + "px"

    styleInstruction.value.height = boundaries['instruction'].height + "px"
    styleInstruction.value.width = boundaries['instruction'].width + "px"
    styleInstruction.value.top = boundaries['instruction'].top + "px"
    styleInstruction.value.left = boundaries['instruction'].left + "px"

    styleReview.value.height = boundaries['review'].height + "px"
    styleReview.value.width = boundaries['review'].width + "px"
    styleReview.value.top = boundaries['review'].top + "px"
    styleReview.value.left = boundaries['review'].left + "px"

    styleEyeGaze.value.height = boundaries['eyegaze'].height + "px"
    styleEyeGaze.value.width = boundaries['eyegaze'].width + "px"
    styleEyeGaze.value.top = boundaries['eyegaze'].top + "px"
    styleEyeGaze.value.left = boundaries['eyegaze'].left + "px"

    const buttonHeight = boundaries['eyegaze'].height * .85 / 4
    var element = document.getElementById("eyegaze_area")
    if (element != null) {
      element.style.setProperty("--move_button_size", buttonHeight + "px");
      element.style.setProperty("--move_button_size_x1_33", (1.33 * buttonHeight) + "px");
      element.style.setProperty("--move_button_size_x2", (2 * buttonHeight) + "px");

    }
  }

  function updateWidthHeights() {

    const availableSpace = availableWidthHeight()
    isPortrait = isScreenPortraitOrientation(availableSpace)
    const boundaries = (isPortrait) ? calculatePortraitWidthHeights(availableSpace) :
                                      calculateLandscapeWidthHeights(availableSpace)
    
    updateStyleObjects(boundaries)

   
  }

  function calculatePortraitWidthHeights(availableSpace) {


    var spaceForHeight = (showState.value == SHOW_PLAY) ?
                  availableSpace.height - INSTRUCTION_FONTSIZE * 7 * 1.2 :
                  availableSpace.height /2
    if (showEyegazeButtons.value)
      spaceForHeight = availableSpace.height * .4

    // reserve space for the instruction lines
    const availableSpaceForBoard = { width : availableSpace.width ,
                height : spaceForHeight,
                isPortrait : true }

    const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)

    const boardHeight = boardDimensions.height
    const boardWidth = boardDimensions.width
    const boardPositionTop = boardDimensions.y
    const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

    const instructionPositionTop = boardPositionTop + boardHeight
    const instructionPositionLeft = availableSpace.leftRightIndent
    const instructionHeight = (showEyegazeButtons.value) ? INSTRUCTION_FONTSIZE * 3.5 : availableSpace.height - instructionPositionTop
    const instructionWidth = availableSpace.width - availableSpace.leftRightIndent * 2

    const buttonPositionTop = instructionPositionTop + instructionHeight
    const buttonPositionLeft = instructionPositionLeft
    const buttonHeight = availableSpace.height - buttonPositionTop
    const buttonWidth = instructionWidth

    const reviewPositionTop = instructionPositionTop
    const reviewPositionLeft = instructionPositionLeft
    const reviewHeight = availableSpace.height - instructionPositionTop
    const reviewWidth = instructionWidth

    const result = { board : {
        width : boardWidth,
        height : boardHeight,
        left : boardPositionLeft + availableSpace.leftIndent,
        top : boardPositionTop+ availableSpace.topIndent
      },
      instruction : {
        width : instructionWidth,
        height : instructionHeight,
        left : instructionPositionLeft + availableSpace.leftIndent,
        top : instructionPositionTop+ availableSpace.topIndent
      },
      eyegaze : {
        width : buttonWidth,
        height : buttonHeight,
        left : buttonPositionLeft + availableSpace.leftIndent,
        top : buttonPositionTop+ availableSpace.topIndent
      },
      review : {
        width : reviewWidth,
        height : reviewHeight,
        left : reviewPositionLeft + availableSpace.leftIndent,
        top : reviewPositionTop+ availableSpace.topIndent
      }
    }
    //console.log("board and movelist dimensions",result)
    return (result)
  }

  function calculateLandscapeWidthHeights(availableSpace) {

    const spaceForWidth = (showState.value == SHOW_PLAY) ? availableSpace.width - 150 :
                               availableSpace.width /2
    // reserve space for the instruction lines
    const availableSpaceForBoard = { width : spaceForWidth,
                height : availableSpace.height ,
                isPortrait : false }
    const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)

    const boardHeight = boardDimensions.height
    const boardWidth = boardDimensions.width
    const boardPositionTop = boardDimensions.y
    const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

    const instructionPositionTop = 0
    const instructionPositionLeft = boardPositionLeft + boardWidth + availableSpace.leftRightIndent
    const instructionHeight = (showEyegazeButtons.value) ? INSTRUCTION_FONTSIZE * 3.5 : availableSpace.height
    const instructionWidth = availableSpace.width - instructionPositionLeft

    const eyegazePositionTop = instructionHeight
    const eyegazePositionLeft = boardPositionLeft + boardWidth + availableSpace.leftRightIndent
    const eyegazeHeight = availableSpace.height - instructionHeight
    const eyegazeWidth = availableSpace.width - eyegazePositionLeft

    const reviewPositionTop = 0
    const reviewPositionLeft = boardPositionLeft + boardWidth + availableSpace.leftRightIndent
    const reviewHeight = availableSpace.height
    const reviewWidth = availableSpace.width - instructionPositionLeft

    const result = { board : {
        width : boardWidth,
        height : boardHeight,
        left : boardPositionLeft + availableSpace.leftIndent,
        top : boardPositionTop+ availableSpace.topIndent
      },
      instruction : {
        width : instructionWidth,
        height : instructionHeight,
        left : instructionPositionLeft + availableSpace.leftIndent,
        top : instructionPositionTop+ availableSpace.topIndent
      },
      eyegaze : {
        width : eyegazeWidth,
        height : eyegazeHeight,
        left : eyegazePositionLeft + availableSpace.leftIndent,
        top : eyegazePositionTop+ availableSpace.topIndent
      },
      review : {
        width : reviewWidth,
        height : reviewHeight,
        left : reviewPositionLeft + availableSpace.leftIndent,
        top : reviewPositionTop+ availableSpace.topIndent
      }

    }
    //console.log("board and movelist dimensions",result)
    return (result)
  }

  function resetEngineScreen() {
    toolbarElements.value = []
    buildToolbar()
  }

  watch(() => windowSizeStore.getWindowSize, () => {
    //console.log('*********** Explorer -- window size changes detected')
    windowSizeRef.value = { width : windowSizeStore.getWindowSize['width'],
                      height : windowSizeStore.getWindowSize['height'] }
    //console.log("*** Explorer new window size",windowSizeRef.value)
    updateWidthHeights()
    menuTrigger.value++
  })

  // separate the "index" field from the offset into the array
  function toolbarArrayOffset(id) {
    for (var i=0; i < toolbarElements.value.length; i++)
      if (toolbarElements.value[i].index == id)
        return (i)
    return (0)
  }

  function moveListClick(moveListFen,rewindDisabled,lastDisabled) {
    //console.log("IN REVIEW ",forceFen.value,rewindDisabled,lastDisabled)

    if (moveListFen != "")
    forceFen.value = moveListFen

    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_REVIEW_REWIND,rewindDisabled)
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_REVIEW_BACK,rewindDisabled)
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_REVIEW_NEXT,lastDisabled)
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_REVIEW_LAST,lastDisabled)

  }

  function toolbarClick(index) {

    //const arrayIndex = toolbarArrayOffset(index)
    //console.log("****** toolbar click index & array offset",index,arrayIndex)

    // trap that the button should be disabled but the screen hasn't caught up
    //if (arrayIndex < 10 && toolbarElements.value[arrayIndex].disabled)
    //  return

    switch (index) {
      case TOOLBAR_REVIEW_DONE : 
        showState.value = SHOW_PLAY
        buildToolbar()
        updateWidthHeights()
        break
      case TOOLBAR_REVIEW_REWIND :
          directionClicked.value = "rewind"
          directionTrigger.value++
          break
        case TOOLBAR_REVIEW_BACK :
          directionClicked.value = "back"
          directionTrigger.value++
          break
        case TOOLBAR_REVIEW_NEXT :
          directionClicked.value = "next"
          directionTrigger.value++
          break
        case TOOLBAR_REVIEW_LAST :
          directionClicked.value = "last"
          directionTrigger.value++
          break

      case TOOLBAR_START_GAME :
        if (gameState.value == GAME_ENDED) {
          // new game
          createNewGame()
        }
        else
        if (gameState.value == NO_GAME) {
          // start game
          createNewGame()
        }
        else {
          // end game
          setupAlert(t("End Game") + "?",t("Do you want to end the current game?"),"yesno","endgame")
        }
        break
      case TOOLBAR_TAKEBACK_OPTIONS :
        if (gameState.value != IN_GAME) {
          if (showBigButtons.value)
            openActionReplacementModal()
          else
            actionSheetTrigger.value++
          break
        }
        
        if (gameState.value == IN_GAME && canUndoBigButtonSelection) {
            getAvailableMoves(fenOfCurrentBoard)
            canTakeBack = false
            canUndoBigButtonSelection = false
            toolbarElements.value[toolbarArrayOffset(TOOLBAR_TAKEBACK_OPTIONS)].disabled = true
            toolbarTrigger.value++
            break
        }
        if (gameState.value == IN_GAME && canTakeBack) {
          //console.log("*************** DID TAKEBACK")
          // did take back. reset to before last player move

          canTakeBack = false
          canUndoBigButtonSelection = false
          getMoveTakeBack()
          toolbarElements.value[toolbarArrayOffset(TOOLBAR_TAKEBACK_OPTIONS)].disabled = true
          toolbarTrigger.value++
        }
        break
      case TOOLBAR_REVIEW :
        if (gameState.value == IN_GAME) {
          //console.log("*** REPLAY MOVES")
          buildReplayStack()
        }
        else {
          //connectionStore.setGameId(openGameId)
          reviewGameId.value = openGameId.toString()
          showState.value = SHOW_REVIEW
          buildToolbar()
          updateWidthHeights()
          //console.log("**** review game id   ",reviewGameId.value)
        }
        break
      case TOOLBAR_REVEAL :
        incrementAppendTrigger()
        forceFen.value = fenOfCurrentBoard + blankAppendTrigger
        if (boardIsRevealed) {
          pieceSet.value = savePieceSet
          boardIsRevealed = false
          toolbarElements.value[toolbarArrayOffset(TOOLBAR_REVEAL)].label = t("Reveal")
        }
        else {
          boardIsRevealed = true
          pieceSet.value = "regular"
          toolbarElements.value[toolbarArrayOffset(TOOLBAR_REVEAL)].label = t("Revert")
        }
        toolbarTrigger.value++
        break
      case TOOLBAR_DONE :
        emit("menu-item-click","big_button_menu",t("Big Button Menu"),"buttons")
        break
    }
  
  }

  // stuff for options

  // Start ACTION LIST

  function actionClick(index) {
    switch (index) {
      case ACTION_BLINDNESS :
        getOptions("blindness")
        break
      case ACTION_ENGINE :
        getOptions("engine")
        break
      case ACTION_SKILL :
        getOptions("skill")
        break
      case ACTION_COLOR :
        getOptions("color")
        break
      case ACTION_REVEAL :
        getOptions("reveal")
        break
    }
  }

  function setupAlert(header,message,type,task) {
    if (showBigButtons.value)
      openAlertReplacementModal(header,message,type,task)
    else {
      alertInfo.value = { header : header, message : message, type : type, task : task  }
      alertTrigger.value++
    }
  }

  function alertResult(response,task) {
    //console.log("Alert Result",response,task)

    if (response == "no")
      return

    switch (task) {
      case "endgame" :
        if (revealEveryMoveNumber > 0) {
          pieceSet.value = "regular"
          boardIsRevealed = true
        }
        instructionString.value = "Game Over"
        setEndGame()
        break
    }
  }

  function actionSheetResult(action,index) {
    if (action != "yes")
      return
    actionClick(index)
  }

  // END Action List

  // START Generic Options List

  const openGenericSingleListModal = async () => {
    const modal = await modalController.create({
      component: ModalGenericSingleList,
      componentProps: {
        displayArray : optionDisplayArray,
        initialValue : optionDisplaySelected,
        title : optionHeading      }
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm') {
      // did user leave the select the same?
      if (data.selected == optionDisplaySelected)
        return

      //console.log("CONFIRM in static",data)

      setOptions(data.selected)
   }
  };
  // END Generic Options List

  // START ACTION SHEET REPLACMENT

   function delayBeforeShowingOption(optionId) {

    let timerSleepAmount = 250

    function oneInterval() {
      clearInterval(id)
      actionClick(optionId)
    }
    var id = setInterval(oneInterval, timerSleepAmount)
  }

 const openActionReplacementModal = async () => {
    buildActionSheetButtons()

    const modal = await modalController.create({
      component: ModalActionReplacement,
      componentProps: {
        displayArray : actionSheetButtons,
        title : t("Options")  
       }
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'selected') {
      delayBeforeShowingOption(data)
    }
  };

  // END ACTION SHEET REPLACEMENT

  // START ALERT REPLACEMENT

  const openAlertReplacementModal = async (header,message,type,task) => {
    const modal = await modalController.create({
      component: ModalAlertReplacement,
      componentProps: {
        header : header,
        message : message,
        alertType : type,
        task : task    
       }
    });

    modal.present();

    const { data, task1 } = await modal.onWillDismiss();

    if (data == "no")
      return
    alertResult(data,task)
  };

  // END ALERT REPLACEMENT


  function tourDone() {
    showTour.value = false
  }

  function setOptions(id) {
    //console.log("set options ",whichOptionIsGenericList,id)

    switch (whichOptionIsGenericList) {
      case "blindness" :
        currentBlindfold = id
        break;
      case "engine" :
        currentEngine = id
        break;
      case "skill" :
        currentSkill = id
        break
      case "color" :
        currentColor = id
        break
      case "takeback" :
        currentTakeback = id
        break
      case "reveal" :
        currentReveal = id
        break
    }
    setBlindfoldOptions()
  }

  function processOptionJsonResponse(json) {
    //console.log(" get blindfold play option callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    currentBlindfold = json["blind_initial"];
    currentEngine = json["engine_initial"];
    currentColor = json["color_initial"];
    currentSkill = json["skill_initial"]
    currentTakeback = json["takeback_initial"];
    currentReveal = json["reveal_initial"];

    var display_text = []
    var values = []
    var initial = ""

    switch (whichOptionIsGenericList) {
      case "blindness" :
        optionHeading = t("Blindfold Difficulty")
        display_text = blindfoldStrings
        values = blindfoldValues
        initial = currentBlindfold
        break;
      case "engine" :
        optionHeading = t("Engine for Opponent")
        display_text = engineStrings
        values = engineValues
        initial = currentEngine
        break;
      case "skill" :
        optionHeading = t("Engine Strength (Skill)")
        display_text = skillStrings
        values = skillValues
        initial = currentSkill
        break;
      case "color" :
        optionHeading = t("Color You Play")
        display_text = colorStrings
        values = colorValues
        initial = currentColor
        break
      case "reveal" :
        optionHeading = t("Reveal Board every # of Moves")
        display_text = revealStrings
        values = revealValues
        initial = currentReveal
        break
    }

    optionDisplayArray = []
    for (var i=0; i < display_text.length; i++) {
      const oneItem = { 
        index : i,
        name : display_text[i],
        id : values[i],
        radio : "" + values[i]
      }
      optionDisplayArray.push(oneItem)
    }
    optionDisplaySelected = "" + initial

    openGenericSingleListModal()
  
  }

  function getOptions(whichOption) {
    whichOptionIsGenericList = whichOption
    optionDisplayArray = []

    var formData = { 
      }

    useGetData("getweb1_blind_options.php", formData, processOptionJsonResponse)
  }

  function incrementAppendTrigger() {
    if (blankAppendTrigger.length > 64)
      blankAppendTrigger = ""
    blankAppendTrigger += " "
  }

  function moveAnimated(arrayIndex) {
    moveHistoryStack.value.push(replayMoveStack[arrayIndex])
    moveHistoryTrigger.value++
  }

  function buildReplayStack() {
    replayMoveStack = []
    replayFenArray.value = [fenBeforeMoveStack]
    for (var i=0; i < moveHistoryStack.value.length; i++) {
      replayFenArray.value.push(moveHistoryStack.value[i].fen)
      replayMoveStack.push(moveHistoryStack.value[i])
    }
    moveHistoryStack.value = []
  }

  function buildMoveHistoryStack() {

    toolbarElements.value[toolbarArrayOffset(TOOLBAR_REVIEW)].disabled = 
            moveCompleteHistoryStack.length == 0

    var i = 0
    firstMoveNumber.value = 1
    fenBeforeMoveStack = INITIAL_FEN
    moveHistoryStack.value = []

    if (moveCompleteHistoryStack.length > 10) {
      if (moveCompleteHistoryStack.length % 2 == 1) {
        i = moveCompleteHistoryStack.length - 11
        firstMoveNumber.value = i / 2 + 1
      }
      else {
        i = moveCompleteHistoryStack.length - 10
        firstMoveNumber.value = i / 2 + 1
      }
    }
    fenBeforeMoveStack = (i == 0) ? INITIAL_FEN : moveCompleteHistoryStack[i-1].fen
    for (; i < moveCompleteHistoryStack.length; i++)
      moveHistoryStack.value.push(moveCompleteHistoryStack[i])
    moveHistoryTrigger.value++

  }

  // boardMove comes back with the piece selected
  function boardMove(arrayIndex,from,to) {
    //console.log("board move ",arguments)
    const oneMove = availableMoves.value[arrayIndex]
    forceFen.value = oneMove.fen
    fenOfCurrentBoard = oneMove.fen
    
    //console.log("one move in boardmove",oneMove)
    moveCompleteHistoryStack.push(oneMove)
    buildMoveHistoryStack()
  
    canTakeBack = true
    canUndoBigButtonSelection = false
    toolbarElements.value[toolbarArrayOffset(TOOLBAR_TAKEBACK_OPTIONS)].disabled = false
    toolbarElements.value[toolbarArrayOffset(TOOLBAR_TAKEBACK_OPTIONS)].label = t("Takeback")
    toolbarTrigger.value++

    if (boardIsRevealed) {
      pieceSet.value = savePieceSet
      boardIsRevealed = false
      toolbarElements.value[toolbarArrayOffset(TOOLBAR_REVEAL)].label = t("Reveal")
      toolbarTrigger.value++
    }

    // if player's SAN has a # then game over with that move
    if (oneMove.san.includes("#"))
      playerMoveCheckMated = true

    getEngineMove(oneMove.fen,oneMove.from,oneMove.to,oneMove.promotion,oneMove.san)
  }

  function processUserInfoJsonResponse(json) {
    //console.log("response user info",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    const blindfold = json["blindness"]
    const color = json["color"]
    const engine = json["engine"]
    const skill = json["skill"]
    const takeback = json["takeback"]
    const reveal = json["reveal"]

    blindfoldString = findValueString(blindfoldStrings,blindfoldValues,blindfold)
    colorString = findValueString(colorStrings,colorValues,color)
    engineString = t("Using") + " " + findValueString(engineNameStrings,engineValues,engine) +
                         " " + t("Engine")
    skillString = findValueString(skillStrings,skillValues,skill)
    revealString = findValueString(revealStrings,revealValues,reveal)

    //console.log("play type",playType)
    if (playType == "computer")
      instructionString.value = colorString + "<br>" + engineString + "<br>" + skillString 
    else
      instructionString.value = blindfoldString + "<br>" + colorString + "<br>" +
        engineString + "<br>" + skillString + "<br>" + revealString
    if (showEyegazeButtons.value)
      instructionString.value += "<br>" + t("Using EyeGaze Buttons")
  }

  function getUserInfo() {
    
    var formData = {
    }
    useGetData("getweb1_blind_user_info.php", formData, processUserInfoJsonResponse)
  }

  function processOpenGameJsonResponse (json) {
    //console.log(" in open game callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    playerMoveCheckMated = false

    moveCompleteHistoryStack = []

    const ucisString = json['ucis']
    const ucis = (ucisString.length == 0) ? [] : json['ucis'].split(",")

    fenAfterLastEngineMove = INITIAL_FEN
    chess.load(INITIAL_FEN)
    for (var i=0; i < ucis.length; i++) {
      const move = chess.move(ucis[i])
      //console.log("MOVE",i,ucis[i],move)
      const oneMove = { san : move.san, fen : move.after }
      moveCompleteHistoryStack.push(oneMove)
      if (i == ucis.length - 1)
        fenAfterLastEngineMove = move.after
    }
    // const sans = json['sans'].split(",")
    // const fens = json['fens'].split(",")

    // for (var i=0; i < sans.length; i++) {
    //   const oneMove = { san : sans[i], fen : fens[i] }
    //   moveCompleteHistoryStack.push(oneMove)
    // }
    buildMoveHistoryStack()

    blindnessLevel = Math.floor(json["level"])
    setPieces(blindnessLevel) 

    revealEveryMoveNumber = Math.floor(json["reveal"])

    playingAsWhite = json["white"]
    orientation.value = (playingAsWhite == 1) ? "white" : "black"

    forceFen.value = fenAfterLastEngineMove
    fenOfCurrentBoard = fenAfterLastEngineMove

    gameState.value = IN_GAME

    buildToolbar()
    toolbarElements.value[TOOLBAR_START_GAME].label = t("End Game")
    toolbarElements.value[TOOLBAR_TAKEBACK_OPTIONS].disabled = true
    toolbarElements.value[TOOLBAR_REVIEW].label = t("Replay")
    toolbarElements.value[TOOLBAR_REVIEW].disabled = false

    if (playType == "blindfold")
      toolbarElements.value[TOOLBAR_REVEAL].disabled = false

    toolbarTrigger.value++

    getAvailableMoves(forceFen.value)
    instructionString.value = t("Your Move")
  }

  function getOpenGame() {
    var formData = {
      gameid : openGameId.toString()
    }
    useGetData("getweb6_load_open_game.php", formData, processOpenGameJsonResponse)
  }

    function processTakeBackJsonResponse (json) {
    //console.log(" in takeback callback",json)
    if (json.hasOwnProperty('error')) {
      explainTourInitialize(null,generateErrorAlertText(json))
      return;
    }
    getOpenGame()
  }

  function getMoveTakeBack() {
    var formData = {
      gameid : openGameId,
    }
    useGetData("get_verbal_do_takeback.php", formData, processTakeBackJsonResponse)
  }

  function processSetOptionsJsonResponse(json) {
    //console.log("response set options",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }
    getUserInfo()
  }

  function setBlindfoldOptions() {

    var formData = {
      blindfold : currentBlindfold,
      engine : currentEngine,
      skill : currentSkill,
      color : currentColor,
      takeback : currentTakeback,
      reveal : currentReveal
    }

    //console.log("formdata for blindfold options",formData)
    useGetData("set51_blind_options.php", formData, processSetOptionsJsonResponse)
  }

  function setPieces(blindnessLevel) {
    switch (blindnessLevel) {
      case 0 :
      case 1 :
        pieceSet.value = "regular"
        break
      case 2 : 
        pieceSet.value = "one_color"
        break
      case 3 :
        pieceSet.value = "two_disks"
        break
      case 4 :
        pieceSet.value = "one_disk"
        break
      case 5 :
        pieceSet.value = "invisible"
        break
    }
    savePieceSet = pieceSet.value
  }

  function processCreateGameJsonResponse (json) {
    console.log(" in create game callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    playerMoveCheckMated = false
    moveNumber = 0
    boardIsRevealed = false
    openGameId = json["gameid"]

    // save game id to local storage so gane can be continued
    connectionStore.setPlayComputerGameId(openGameId)
    connectionStore.setPlayComputerBlindfold(playType)

    blindnessLevel = Math.floor(json["level"])
    setPieces(blindnessLevel) 
    //console.log("******* PIECESET",blindnessLevel,pieceSet.value)

    playingAsWhite = json["white"]
    revealEveryMoveNumber = Math.floor(json["reveal"])

    //console.log("reveal every ",revealEveryMoveNumber)

    orientation.value = (playingAsWhite == 1) ? "white" : "black"

    incrementAppendTrigger()
    forceFen.value = INITIAL_FEN + blankAppendTrigger
    fenAfterLastEngineMove = INITIAL_FEN
    availableMoves.value = []

    gameState.value = IN_GAME
    
    instructionString.value = t("")
    canTakeBack = false
    buildToolbar()
    toolbarElements.value[toolbarArrayOffset(TOOLBAR_START_GAME)].label = t("End Game")

    if (playType == "blindfold")
      toolbarElements.value[toolbarArrayOffset(TOOLBAR_REVEAL)].disabled = false

    toolbarElements.value[toolbarArrayOffset(TOOLBAR_TAKEBACK_OPTIONS)].disabled = true

    toolbarElements.value[toolbarArrayOffset(TOOLBAR_REVIEW)].disabled = true
    toolbarElements.value[toolbarArrayOffset(TOOLBAR_REVIEW)].label = t("Replay")

    toolbarTrigger.value++

    engineMoveJson = null

    fenOfCurrentBoard = INITIAL_FEN

    moveCompleteHistoryStack = []
    buildMoveHistoryStack()

    if (playingAsWhite == 1) {
      getAvailableMoves(INITIAL_FEN)
    }
    else
      getEngineMove(INITIAL_FEN,0,0,"","")
  }

  function createNewGame() {

    moveCompleteHistoryStack = []
    buildMoveHistoryStack()

    var formData = { 
      blindfold : (playType == "blindfold") ? 1 : 0
    }

    useGetData("getweb2_create_blindfold_game.php", formData,processCreateGameJsonResponse)
  }

  function showConfetti() {
    var element = document.getElementById("app")

    party.resolvableShapes["whiteKing"] = "<img src='img/chesspieces/wikipedia/wK.svg'/>"
    party.resolvableShapes["whiteQueen"] = "<img src='img/chesspieces/wikipedia/wQ.svg'/>"
    party.resolvableShapes["blackKing"] = "<img src='img/chesspieces/wikipedia/bK.svg'/>"
    party.resolvableShapes["blackQueen"] = "<img src='img/chesspieces/wikipedia/bQ.svg'/>"

    party.sparkles(element, {
      count: party.variation.range(100, 200),
      speed: party.variation.range(5, 20),
      shapes: ["whiteKing","whiteQueen","blackKing","blackQueen"]
    });
  }

  function eyegazeClick(eyegazeInfo) {
    // console.log("BUTTON CLICKED", eyegazeInfo.moves,availableMoves.value.length)
    // console.log("button move",eyegazeInfo.moves[0])
    // console.log("want fen",eyegazeInfo.moves[0].after)
    // console.log("available moves",availableMoves.value)
    if (eyegazeInfo.moves.length == 1) {
      for (var i=0; i < availableMoves.value.length; i++)
        if (eyegazeInfo.moves[0].after == availableMoves.value[i].fen) {
          //console.log("*** BUTTON MOVE MATCH",i)
          boardMove(i)
          break
        }
    }
    else {
      canUndoBigButtonSelection = true
      eyegazeMatrix.value = buildEyegazeButtons(eyegazeInfo.moves,isPortrait,blindnessLevel)
      toolbarElements.value[TOOLBAR_TAKEBACK_OPTIONS].disabled = false
      toolbarElements.value[toolbarArrayOffset(TOOLBAR_TAKEBACK_OPTIONS)].label = t("Undo")
      toolbarTrigger.value++
    }

  }

  function getAvailableMoves(fen) {
    var fenPieces = fen.split(" ")
    fenPieces[5] = 1
    const fenFixed = fenPieces.join(" ")
    chess.load(fenFixed)
    const moves = chess.moves({ verbose: true })
    availableMoves.value = buildAvailableMoves(moves,false)

    if (showEyegazeButtons.value)
      eyegazeMatrix.value = buildEyegazeButtons(moves,isPortrait,blindnessLevel)
  }

  function buildReplayHistory() {
    var isWhiteMove = true
    var replayString = ""
    var firstIndex = 0
    var moveCounter = 0
    replayHistorySans = []
    replayHistoryFens = []
    firstReplayMoveNumber = 1
    fenBeforeReplay = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
    
    if (moveCompleteHistoryStack.length > 10) {
        if (moveCompleteHistoryStack.length % 2 == 1) {
            firstIndex = moveCompleteHistoryStack.length - 11
            firstReplayMoveNumber = firstIndex / 2 + 1
        }
        else {
            firstIndex = moveCompleteHistoryStack.length - 10
            firstReplayMoveNumber = firstIndex / 2 + 1
        }
    }
    
    if (firstIndex > 0) {
        fenBeforeReplay = moveCompleteHistoryStack[firstIndex-1].fen
    }
    replayHistoryFens.push(fenBeforeReplay)
    moveCounter = firstReplayMoveNumber
    for (var i = firstIndex; i < moveCompleteHistoryStack.length; i++) {
        if (isWhiteMove) {
            if (i != firstIndex) {
                replayString += " "
            }
            replayString += String(moveCounter) + "."
            moveCounter += 1
        }
        replayString += " " + moveCompleteHistoryStack[i].san
        replayHistoryFens.push(moveCompleteHistoryStack[i].fen)
        replayHistorySans.push(moveCompleteHistoryStack[i].san)
        
        isWhiteMove = !isWhiteMove
    }
    return (replayString)
  }

  function handleGameOver(result) {
    // result is: "engine", "player", "stalemate"

    if (playType == "blindfold") {
      pieceSet.value = "regular"
      boardIsRevealed = true
      toolbarElements.value[toolbarArrayOffset(TOOLBAR_REVEAL)].label = t("Revert")
      toolbarTrigger.value++
    }

    const thumbDown = "&nbsp;<i class='fa fa-thumbs-down'></i>&nbsp;"
    const thumbUp = "&nbsp;<i class='fa fa-thumbs-up'></i>&nbsp;"
    const star = "&nbsp;<i class='fa fa-thumbs-up'></i>&nbsp;"
    var resultString = ""
    switch (result) {
      case "player" :
        instructionString.value += "<br>" + star +star + t("You Checkmated") + "!"  + star + star
        if (isSubscriber.value)
          showConfetti()  
        break
      case "engine" :
        instructionString.value += "<br>" + thumbDown + thumbDown + t("You Got Checkmated") + 
              thumbDown + thumbDown
        break
      case "stalemate" :
        instructionString.value += "<br>" + thumbUp + thumbDown + t("Stalemate") + 
              thumbDown + thumbUp
        break
    }
    setEndGame()
  }

  function applyEngineMoveFromVerbalCode() {

    // did player checkmate on last move?
    if (playerMoveCheckMated) {
      handleGameOver("player")
      return
    }

    chess.load(fenOfCurrentBoard)
    const moves = chess.moves({ verbose: true })

    // if player didn't checkmate and engine has no moves then stalemoate
    if (moves.length == 0) {
      handleGameOver("stalemate")
      return
    }

    const engineFen = engineMoveJson['engine_fen']
    
    // the engine had moves but didn't make one, that seems bad
    if (engineFen == "") {
      setupAlert("Error","Engine didn't return with a move","alert")
      return
    }

    var engineChessJS = null

    // load the last player move, then get the possible engine moves against that
    //          find the move the engine made and use it's "move" record

    const engineFenParts = engineFen.split(" ")
    for (var i=0; i < moves.length; i++) {
      const fenParts = moves[i].after.split(" ")
      if (engineFenParts[0] == fenParts[0]) {
        engineChessJS = moves[i]
        break
      }
    }

    if (engineChessJS == null) {
      setupAlert("Error","Engine made a move but it isn't valid","alert")
      return
    }

    const oneMove = { fen : engineChessJS.after, san : engineChessJS.san, 
                          chessjs : engineChessJS}

    moveCompleteHistoryStack.push(oneMove)
    buildMoveHistoryStack()

    // see if it should be revealed

    const moveNumber = (playingAsWhite == 1) ? Math.floor(moveCompleteHistoryStack.length/2) : 
              Math.floor(moveCompleteHistoryStack.length+1)/2
    if (playType == "blindfold" && moveNumber > 1 && revealEveryMoveNumber > 0 &&
                        (moveNumber % revealEveryMoveNumber) == 0) {

      boardIsRevealed = true
      pieceSet.value = "regular"
    }
    animateFen.value = [forceFen.value , engineChessJS.after]

    fenOfCurrentBoard = engineChessJS.after
    
    // did the engine checkmate the player?
    if (engineChessJS.san.includes("#")) {
      handleGameOver("engine")
      return
    }
    getAvailableMoves(engineChessJS.after)
    
    // did engine cause a stalemate?
    if (availableMoves.value.length == 0) {
      handleGameOver("stalemate")
      return
    }

    toolbarElements.value[TOOLBAR_REVIEW].disabled = moveCompleteHistoryStack.length == 0
    toolbarTrigger.value++
  }

  function runDelayAfterReceiveEngineMove() {

    let timerSleepAmount = 500

    function oneInterval() {
      clearInterval(id)

      applyEngineMoveFromVerbalCode()
    }
    var id = setInterval(oneInterval, timerSleepAmount)
  }
  function processEngineMoveJsonResponse(json) {
    console.log(" in engine move callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    engineMoveJson = json
    runDelayAfterReceiveEngineMove()
  }

  function getEngineMove(fen,from_square,to_square,promotion,san) {

    engineMoveJson = null
    availableMoves.value = []
    eyegazeMatrix.value = [[]]

    var formData = {
      gameid : openGameId,
      fen : fen,
      from_square : from_square,
      to_square : to_square,
      promotion : promotion,
      san : san,
      source : "c"
    }
    //
    
    console.log("in get engine move ",formData)
    useGetData("chessvis_engine_move.php", formData,
                        processEngineMoveJsonResponse)
  }

  function processSetEndGameJsonResponse (json) {
    //console.log(" in set end game callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    pieceSet.value = "regular"
    boardIsRevealed = true

    gameState.value = NO_GAME
    availableMoves.value = []

    buildToolbar()
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_TAKEBACK_OPTIONS,false)
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_REVIEW,false)
    setToolbarElementLabel(toolbarElements.value,TOOLBAR_REVIEW,t("Review"))   
    setToolbarElementLabel(toolbarElements.value,TOOLBAR_START_GAME,t("New Game"))   

    if (playType == "blindfold")
      setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_REVEAL,true)
    toolbarTrigger.value++

  }

  function setEndGame() {

    // clear game id from local storage because the game ended
    connectionStore.setPlayComputerGameId("0")
    connectionStore.setPlayComputerBlindfold("")
    
    availableMoves.value = []

    var formData = {
      gameid : openGameId
    }
    useGetData("set18_game_ended.php", formData, processSetEndGameJsonResponse)
  }

</script>

<style scoped>

</style>